/**
 * Cancelled Items Hooks
 *
 * TanStack Query hooks for cancelled items data and processing mutations.
 */
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { CancelledItem } from '../types';

/**
 * Hook to fetch one page of cancelled items for a location.
 * Server-paginated (RPC-backed): the old unpaginated read broke at busy
 * locations — the id list overflowed the PostgREST URL limit and the whole
 * tab 500'd. `total` is computed by the same RPC, so badge == list == pager.
 */
export function useCancelledItems(locationId: string, page = 1, pageSize = 25) {
  const query = trpc.productionOrders.issues.getCancelledItems.useQuery(
    { location_id: locationId, limit: pageSize, offset: (page - 1) * pageSize },
    {
      enabled: !!locationId,
      refetchInterval: 30_000, // Refresh every 30 seconds
    }
  );

  const data = query.data as { items: CancelledItem[]; total: number } | undefined;
  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to process a cancelled item (return to shelf or mark as defect)
 */
export function useProcessCancelledItem() {
  const utils = trpc.useUtils();
  const { t } = useTranslation('production');

  return trpc.productionOrders.issues.processCancelledItem.useMutation({
    onSuccess: (_data, variables) => {
      // Invalidate the cancelled items list to refresh UI
      utils.productionOrders.issues.getCancelledItems.invalidate();

      // Show success message based on action
      const actionMessage =
        variables.action === 'return_to_shelf'
          ? t('issues.cancelled.returnedToShelf')
          : variables.action === 'add_to_preprint'
            ? t('issues.cancelled.addedToPreprint')
            : t('issues.cancelled.markedDefect');
      toast.success(actionMessage);
    },
    onError: (error) => {
      toast.error(t('issues.cancelled.processFailed', { message: error.message }));
    },
  });
}

/**
 * Hook to bulk-process a page-scoped selection of cancelled items — the SAME
 * per-unit disposition as `useProcessCancelledItem`. The caller renders the
 * honest `{done, failed}` outcome itself (toast + inline text), so this hook
 * only invalidates the list + the tab badge count on completion.
 */
export function useProcessCancelledItemsBulk() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.processCancelledItemsBulk.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.getCancelledItems.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
    },
  });
}
